import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { ValueType } from 'react-select';
import { Button, Form, Label, Progress } from 'reactstrap';
import { Dispatch } from 'redux';
import { importXML } from '../../stores/database/actionCreators';
import { getImportKey, getImportProgress, getImportStatus, getImportTextStatus } from '../../stores/database/selectors';
import { ImportStatus } from '../../stores/database/state';
import { RootState } from '../../stores/store';
import FormInput from '../common/FormInput';
import ConfirmModal from '../common/modals/ConfirmModal';
import ViewContainer from '../layout/ViewContainer';
import WorkspaceSelect from '../workspaces/WorkspaceSelect';

export interface Props extends LocalizeContextProps, RouteComponentProps {
    status: ImportStatus;
    importTextStatus?: string;
    progress?: number;
    key?: string;

    importXML: (settings: { hostname: string, secure: boolean, workspaceId: string }, formData: FormData) => void;
}

export interface State {
    showModal: boolean;
    hostname: string;
    secure: boolean;
    workspace?: { label: string; value: string; };
    server?: { label: string; value: string; };
}

class DatabaseImportPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hostname: '',
            secure: true,
            showModal: false,
        };
    }

    public render(): JSX.Element {
        const t = this.props.translate;
        return (
            <ViewContainer>
                <h1 className="title">{t('database.import.header.importXML')}</h1>

                {this.props.status === ImportStatus.Start ? (
                    <Form id="fileForm">
                        <div className="form-group">
                            <Label for="from-db" className="control-label" />
                            <input name="db" id="form-db" type="file" />
                        </div>
                        <div className="spacer" />
                        <Button color="primary" onClick={() => this.setState({ showModal: true })}>
                            <FontAwesomeIcon className="mr-2" icon={['fal', 'upload']} />
                            {t('database.import.button.upload')}
                        </Button>

                    </Form>
                ) : (
                        <div>
                            <p>{this.props.importTextStatus ?? 'Uploading...'}</p>
                            <Progress
                                color={this.props.status === ImportStatus.Error ? 'danger' : 'primary'}
                                style={{ marginBottom: '10px' }}
                                value={(this.props.progress ?? 0) * 100}
                            />
                            {this.props.key ?
                                <div>Key: {this.props.key}</div> : null
                            }
                            {this.props.status === ImportStatus.Success ? (
                                <Link
                                    to={{
                                        pathname: '/users',
                                        state: { from: this.props.location }
                                    }}
                                >
                                    {t('database.import.link.back')}
                                </Link>
                            ) : null}
                        </div>
                    )}
                <ConfirmModal
                    confirmText={t('modal.assign') as string}
                    cancel={this.toggle}
                    header="Assign Workspaces"
                    className="overflow-visible"
                    confirm={() => { this.upload(); this.toggle(); }}
                    isOpen={this.state.showModal}
                >
                        <FormInput
                            name="hostname"
                            fullName={t('database.import.field.hostname') as string}
                            value={this.state.hostname || ''}
                            onChange={this.handleInputChange}
                        />
                        <FormInput
                            name="secure"
                            fullName={t('database.import.field.secure') as string}
                            checked={this.state.secure}
                            type="checkbox"
                            onChange={this.handleInputChange}
                        />
                        <hr />
                        <p>{t('database.import.modal.assignWorkspace.text')}</p>
                        <WorkspaceSelect
                            selected={''}
                            updateSelection={this.updateWorkspaceSelection}
                            selection={this.state.workspace}
                        />
                </ConfirmModal>
            </ViewContainer>
        );
    }

    private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            [event.target.name]: event.target.type === 'checkbox' ?
                event.target.checked : event.target.value,
            // tslint:disable-next-line:no-any
        } as any);
    }

    private updateWorkspaceSelection = (selection: ValueType<{ label: string, value: string }> |
    { label: string, value: string }) => {
        this.setState({ workspace: selection as { label: string, value: string } });
    }

    private toggle = () => {
        this.setState({ showModal: !this.state.showModal });
    }

    private upload = () => {
        const formData = new FormData(document.getElementById('fileForm') as HTMLFormElement);
        const selected = this.state.workspace;
        if (selected) {
            this.props.importXML(
                {
                    hostname: this.state.hostname,
                    secure: this.state.secure,
                    workspaceId: selected.value,
                },
                formData
            );
        }
    }
}

const mapStateToProps = (state: RootState) => ({
    importTextStatus: getImportTextStatus(state),
    key: getImportKey(state),
    progress: getImportProgress(state),
    status: getImportStatus(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    importXML: (
        settings: { hostname: string, secure: boolean, workspaceId: string },
        formData: FormData
    ) => dispatch(importXML(settings, formData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withLocalize(withRouter(DatabaseImportPage)));